import { type FC, type ReactNode, useMemo } from 'react';

import { Provider } from 'urql';

import { LtiError } from '@/src/features/auth/components/errors/LtiError';
import { LtiLoading } from '@/src/features/auth/components/LtiLoading';
import { useLtiUserData } from '@/src/features/auth/hooks';
import { useLogger } from '@/src/hooks/internal/useLogger';
import { UrqlLtiClient } from '@/src/integrations/urql/client';

enum UrqlClientState {
  Error = 'Error',
  Loading = 'Loading',
  Success = 'Success',
}

type MemoizedUrqlClient =
  | { readonly client: UrqlLtiClient; readonly state: UrqlClientState.Success }
  | { readonly message: string; readonly state: UrqlClientState.Error }
  | { readonly state: UrqlClientState.Loading };

export const UrqlLtiProvider: FC<{ readonly children: ReactNode }> = ({ children }) => {
  const ltiUserData = useLtiUserData();
  const logger = useLogger();

  const urqlClientState = useMemo<MemoizedUrqlClient>(() => {
    if (ltiUserData === undefined) {
      return { state: UrqlClientState.Loading };
    }
    if (ltiUserData === null) {
      logger.error('The LTI request did not include LTI user data.');
      return {
        message: 'The LTI request did not include LTI user data.',
        state: UrqlClientState.Error,
      };
    }
    return {
      client: new UrqlLtiClient({ ltiUserData }),
      state: UrqlClientState.Success,
    };
  }, [ltiUserData, logger]);

  if (urqlClientState.state === UrqlClientState.Loading) {
    return <LtiLoading />;
  }

  if (urqlClientState.state === UrqlClientState.Error) {
    return <LtiError errorMessage={urqlClientState.message} />;
  }

  return <Provider value={urqlClientState.client}>{children}</Provider>;
};
